import { fetchStore } from 'api/Store/actions'
import { Loader, Modal, StoreDetails, Table } from 'components'
import { ECOM_CLUSTERS } from 'constants.js'
import { selectRequestParams } from 'data/selectors'
import moment from 'locale/momentLocale'
import React, { useCallback } from 'react'
import { withTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from 'store/types'
import type { Translate } from 'types'
import { useToggle } from 'utils'

import { CompanyDelayed, Date, Hour, Reason } from './CellComponents'
import { ACTIVE_CLOSED_TAB, DELAYED_RTLOG_TAB, DELAYED_STORES_TAB, ZERO_REPORTING_TAB } from './constants'
import DelayedDetails from './DelayedDetails'
import useStyles from './useStyles'

interface Props {
  delayedStores: any[]
  generalRevenue: any
  isFetchingCompletenessGeneralRevenue: boolean
  isFetchingDelayedStores: boolean
  t: Translate
  todayDateChosen: boolean
  variant: string
}

function DelayedStores({
  delayedStores,
  generalRevenue,
  isFetchingCompletenessGeneralRevenue,
  isFetchingDelayedStores,
  t: translate,
  todayDateChosen,
  variant,
}: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { countryId, storeId } = useSelector((state: State) => state.componentsCompleteness.details)
  const requestParams = useSelector(selectRequestParams)
  const { isToggled, toggle } = useToggle()
  const onRowClick = useCallback(
    ({ countryId, storeId }: any) => {
      dispatch(
        fetchStore({
          ...requestParams,
          countryId,
          storeId,
        }),
      )
      toggle()
    },
    [dispatch, requestParams, toggle],
  )

  function getStoresCount() {
    const { storesDelayedECom, storesDelayedNECom } = delayedStores.reduce(
      (acc: any, { cluster }: any) => {
        const isECom = ECOM_CLUSTERS.includes(cluster)

        return {
          storesDelayedECom: isECom ? acc.storesDelayedECom + 1 : acc.storesDelayedECom,
          storesDelayedNECom: !isECom ? acc.storesDelayedNECom + 1 : acc.storesDelayedNECom,
        }
      },
      { storesDelayedECom: 0, storesDelayedNECom: 0 },
    )
    const storesECom = variant === ACTIVE_CLOSED_TAB ? generalRevenue?.storesCurrentEComActive : generalRevenue?.storesCurrentECom
    const storesNECom =
      variant === ACTIVE_CLOSED_TAB ? generalRevenue?.storesCurrentNEComActive : generalRevenue?.storesCurrentNECom
    const countFromList = [ACTIVE_CLOSED_TAB, DELAYED_RTLOG_TAB, ZERO_REPORTING_TAB].includes(variant)
    const delayedECom = countFromList ? storesDelayedECom : generalRevenue?.delayedECom
    const delayedNECom = countFromList ? storesDelayedNECom : generalRevenue?.delayedNECom

    return {
      delayedECom,
      delayedNECom,
      storesECom,
      storesNECom,
    }
  }

  const sortedStores = delayedStores
    .map(store => ({
      ...store,
      timestamp: store.timestamp,
      countryId: store.countryId,
      countryName: `${store.storeChannel} ${store.storeName}`,
      storeId: store.storeId,
    }))
    .sort((a, b) => moment(b.timestamp).diff(a.timestamp, 'seconds') || a.countryName.localeCompare(b.countryName))
  const delayedCells = [
    {
      key: 'timestamp',
      title: translate('common.date'),
      CellRender: Date,
    },
    {
      key: 'hour',
      title: translate('revenue.stats.completeness.hour'),
      CellRender: Hour,
    },
  ]
  const activeClosedCells = [
    {
      key: 'calendarException',
      title: translate('revenue.stats.completeness.reason'),
      CellRender: Reason,
      isDefaultSort: true,
    },
  ]
  const columns = [
    {
      key: 'storeName',
      title: translate('revenue.stats.completeness.store'),
      CellRender: CompanyDelayed,
    },
    ...(todayDateChosen && [DELAYED_STORES_TAB, DELAYED_RTLOG_TAB].includes(variant) ? delayedCells : []),
    ...(variant === ACTIVE_CLOSED_TAB ? activeClosedCells : []),
  ]
  const { delayedECom, delayedNECom, storesECom, storesNECom } = getStoresCount()

  return (
    <>
      <DelayedDetails
        delayedECom={delayedECom}
        delayedNECom={delayedNECom}
        isFetching={isFetchingCompletenessGeneralRevenue || isFetchingDelayedStores}
        storesECom={storesECom}
        storesNECom={storesNECom}
        todayDateChosen={todayDateChosen}
        variant={variant}
      />
      {isFetchingDelayedStores && <Loader />}
      {!isFetchingDelayedStores && (
        <div className={classes.tableWrapper}>
          <Table columns={columns} data={sortedStores} onRowClick={onRowClick} tableClassName="completeness-details-table" />
        </div>
      )}
      {countryId && isToggled && (
        <Modal hideModal={toggle}>
          <StoreDetails countryId={countryId} hideModal={toggle} storeId={storeId} />
        </Modal>
      )}
    </>
  )
}

export default withTranslation()(DelayedStores)
